import type { AdminPayoutListItemDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { initials, shortId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatCurrency, formatDateTime } from '../../../utils/format';
import { PayoutRailBadge } from '../_components/PayoutRailBadge';

/**
 * Column definitions for the payouts DataGrid (AUT-128), extracted from
 * the page so the page component stays under the line cap. Sorting is
 * disabled per column — the backend list is fixed `createdAt DESC` (no
 * arbitrary sort), so the grid never sends a sort the server can't honour.
 *
 * Columns mirror the mockup `payouts-queue.html`: Выплата (№ + short id) /
 * Инспектор (avatar + name) / Сумма (right-aligned monospace money) /
 * Рельс (display-only badge) / Причина ошибки (danger red; em-dash when
 * not failed) / Создана.
 *
 * Identity cell (inspector) renders through `<RedactedField perm=
 * "view_pii">`, which agrees with `row.piiRevealed` (both derive from the
 * same backend permission). A revealed value is never re-redacted; only
 * revealed values get a copy button.
 */

/** Inspector cell: avatar (2-letter initials) + name + truncated short id. */
const InspectorCell = ({ row, t }: { row: AdminPayoutListItemDto; t: TFunction }): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <Avatar
      sx={{
        width: 30,
        height: 30,
        fontSize: 11,
        fontWeight: 700,
        bgcolor: 'info.light',
        color: 'info.dark',
      }}
    >
      {initials(row.inspectorName)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      {row.inspectorName ? (
        <Typography variant="body2" fontWeight={600} noWrap component="span">
          <RedactedField
            perm="view_pii"
            value={row.inspectorName}
            redacted={row.inspectorName}
            {...(row.piiRevealed ? { copyable: true } : {})}
          />
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }} noWrap>
          {t('payouts.noValue')}
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
        {shortId(row.inspectorId)}
      </Typography>
    </Box>
  </Box>
);

/** Payout-number + short id cell (mono font per mockup `.u-id` style). */
const PayoutCell = ({ row }: { row: AdminPayoutListItemDto }): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
      {`№ ${row.payoutNumber}`}
    </Typography>
    <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
      {shortId(row.id)}
    </Typography>
  </Box>
);

export const buildPayoutColumns = (t: TFunction): GridColDef<AdminPayoutListItemDto>[] => [
  {
    field: 'payout',
    headerName: t('payouts.columns.payout'),
    flex: 0.9,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }) => <PayoutCell row={row} />,
  },
  {
    field: 'inspector',
    headerName: t('payouts.columns.inspector'),
    flex: 1.3,
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => <InspectorCell row={row} t={t} />,
  },
  {
    field: 'amount',
    headerName: t('payouts.columns.amount'),
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => (
      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
        {formatCurrency(Number(row.amount), row.currency)}
      </Typography>
    ),
  },
  {
    field: 'rail',
    headerName: t('payouts.columns.rail'),
    flex: 0.5,
    minWidth: 110,
    sortable: false,
    renderCell: ({ row }) => <PayoutRailBadge rail={row.rail} />,
  },
  {
    field: 'failedReason',
    headerName: t('payouts.columns.failedReason'),
    flex: 1.1,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) =>
      row.failedReason ? (
        <Typography variant="body2" color="error.main" noWrap>
          {row.failedReason}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
          {t('payouts.noValue')}
        </Typography>
      ),
  },
  {
    field: 'createdAt',
    headerName: t('payouts.columns.created'),
    flex: 0.8,
    minWidth: 150,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {formatDateTime(row.createdAt)}
      </Typography>
    ),
  },
];
